import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@material-tailwind/react";
import { Helmet } from "react-helmet";
import { PropertiesProvider } from "./contexts/PropertiesContext.js";

// Pages
import Diagnostics from "./pages/Diagnostics";
import Documents from "./pages/Documents";
import Invoices from "./pages/Invoices";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import DPE from "./pages/DPE";
import FiscalId from "./pages/FiscalId";
import Appointment from "./pages/Appointment";
import Quote from "./pages/Quote";
import SignQuote from "./pages/SignQuote";
import TechnicalHelp from "./pages/TechnicalHelp.js";
import Support from "./pages/Support.js";
import ConsentShare from "./pages/ConsentShare.js";
import Deals from "./pages/Deals";
import Suggestion from "./pages/Suggestion";
import Certification from "./pages/Certification";
import Reviews from "./pages/Reviews";
import Referral from "./pages/Referral";
import SimulationDPE from "./pages/SimulationDPE";
import ActionsPage from "./pages/Actions";
import BankImprintPage from "./pages/BankImprint";
import LittleWorkerOfferPage from "./pages/LittleWorkerOfferPage";

// Iubenda configuration
const iubConfig = {
  askConsentAtCookiePolicyUpdate: true,
  floatingPreferencesButtonDisplay: "anchored-center-left",
  googleAdsDataRedaction: false,
  lang: "fr",
  perPurposeConsent: true,
  purposes: "1,2,3,4,5",
  reloadOnConsent: false,
  siteId: 2034262,
  storage: { useSiteId: true },
  cookiePolicyId: 27757072,
  i18n: {
    fr: {
      banner: {
        title: "Chez Dimo Diagnostic  on aime les Cookies 🍪",
        dynamic: {
          body: `<div id="iubenda-cs-paragraph"><strong>Afin de vous proposer la meilleure expérience possible sur notre site web</strong>, nous utilisons des cookies techniques et statistiques pour suivre son fonctionnement et mesurer son audience.\nEn cliquant sur <strong>"Accepter et poursuivre la navigation"</strong>, vous nous permettez de suivre les visites sur le site et de vous proposer des contenus adaptés.\nVous avez la possibilité d'accepter ou de refuser tout ou partie des cookies déposés par <strong>DIMO Diagnostic</strong>. \n<br><a href="https://www.dimo-diagnostic.net/protection-des-donnees">Lire notre politique relative de confidentialité</a>\nPas de stress ! Vous pourrez à tout moment modifier vos choix et paramétrer vos cookies.\n</div>`
        }
      }
    }
  },
  cookiePolicyUrl: "https://www.dimo-diagnostic.net/protection-des-donnees",
  banner: {
    acceptButtonCaptionColor: "#042C2B",
    acceptButtonColor: "#0BFF80",
    acceptButtonDisplay: true,
    applyStyles: false,
    backgroundColor: "#F5FEFB",
    backgroundOverlay: true,
    closeButtonRejects: false,
    customizeButtonCaptionColor: "#424A51",
    customizeButtonColor: "#DDEDE8",
    customizeButtonDisplay: true,
    explicitWithdrawal: false,
    fontSizeBody: "12px",
    fontSizeCloseButton: "10px",
    listPurposes: true,
    logo: null,
    linksColor: "#A7E3C0",
    position: "float-center",
    rejectButtonCaptionColor: "#042C2B",
    rejectButtonColor: "#0BFF80",
    rejectButtonDisplay: false,
    slideDown: false,
    textColor: "#24292E",
    usesThirdParties: false,
    acceptButtonCaption: "Accepter et Poursuivre la navigation >",
    cookiePolicyLinkCaption: "politique relative aux cookies",
    customizeButtonCaption: "En savoir plus et personnaliser",
    rejectButtonCaption: `<button class="iubenda-cs-accept-btn iubenda-cs-btn-primary" tabindex="0" role="button">Continuer sans accepter</button>`
  },
  callback: {
    onConsentGiven: (...args) => {
      const prefs = window._iub?.cs?.api?.getPreferences?.();
      if (prefs?.purposes) {
        const analytics = prefs.purposes['3'] ? 'granted' : 'denied';
        const ads       = prefs.purposes['4'] ? 'granted' : 'denied';
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            analytics_storage: analytics,
            ad_storage:       ads,
            ad_user_data:     ads,
            ad_personalization: ads
          });
          window.gtag('config', 'G-LEBBYZ33NK', { page_path: window.location.pathname });
        } else {
          console.warn('gtag function not found when trying to update consent.');
        }
      } else {
        console.warn('Could not get purposes from Iubenda preferences.');
      }
    }
  }
};

// Expose Iubenda config
if (typeof window !== 'undefined') {
  window._iub = window._iub || [];
  window._iub.csConfiguration = iubConfig;
}

function App() {
  // Optionnel: listener TCF
  useEffect(() => {
    let listenerId = null;
    const handleTcf = (data, success) => {
      if (data.listenerId) listenerId = data.listenerId;
      console.log('[TCF API Listener]', success, data);
      // TODO: mapper TCF vers gtag('consent','update') si nécessaire
    };
    if (typeof window.__tcfapi === 'function') {
      window.__tcfapi('addEventListener', 2, handleTcf);
    }
    return () => {
      if (typeof window.__tcfapi === 'function' && listenerId !== null) {
        window.__tcfapi('removeEventListener', 2, () => {}, listenerId);
      }
    };
  }, []);

  return (
    <ThemeProvider>
      <Helmet>
        <title>Espace client - Dimo Diagnostic</title>
        <meta name="description" content="Bienvenue sur l'application Dimo Diagnostic." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://client.dimo-diagnostic.net" />

        {/* 1) Charger la librairie gtag.js */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-LEBBYZ33NK"></script>
        {/* 2) Initialiser dataLayer + Consent Mode par défaut */}
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage:       'denied',
            ad_user_data:     'denied',
            ad_personalization: 'denied'
          });
          gtag('js', new Date());
          gtag('config', 'G-LEBBYZ33NK');
        `}</script>
        {/* 3) Iubenda Cookie Solution */}
        <script async src="//cdn.iubenda.com/cs/iubenda_cs.js" charSet="UTF-8"></script>
        <script async src="//cs.iubenda.com/sync/2034262.js"></script>
      </Helmet>

      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/consent/:token/:action" element={<ConsentShare />} />
          <Route path="/public-quote/:token" element={<Quote isPublic />} />
          <Route path="/public-quote/:token/sign" element={<SignQuote isPublic />} />
          <Route path="/login-2" element={<Login />} />
          <Route path="/*" element={<PropertiesProvider><Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/diagnostics/:id" element={<Diagnostics />} />
            <Route path="/diagnostics" element={<Diagnostics />} />
            <Route path="/fiscal-id/:id" element={<FiscalId />} />
            <Route path="/fiscal-id" element={<FiscalId />} />
            <Route path="/dpe/:id" element={<DPE />} />
            <Route path="/dpe" element={<DPE />} />
            <Route path="/appointment/:id" element={<Appointment />} />
            <Route path="/appointment" element={<Appointment />} />
            <Route path="/documents/:id" element={<Documents />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/quote/:id/sign" element={<SignQuote />} />
            <Route path="/quote/:id" element={<Quote />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/technical-help" element={<TechnicalHelp />} />
            <Route path="/suggestion" element={<Suggestion />} />
            <Route path="/support/:id" element={<Support />} />
            <Route path="/support" element={<Support />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/actions/:id" element={<ActionsPage />} />
            <Route path="/actions" element={<ActionsPage />} />
            <Route path="/certification/:departement?" element={<Certification />} />
            <Route path="/bank-imprint/:id" element={<BankImprintPage />} />
            <Route path="/bank-imprint" element={<BankImprintPage />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/referral" element={<Referral />} />
            <Route path="/simulation-dpe" element={<SimulationDPE />} />
            <Route path="/little-worker" element={<LittleWorkerOfferPage />} />
            <Route path="/little-worker/:propertyId" element={<LittleWorkerOfferPage />} />
            <Route path="*" element={<NotFound />} status={404} />
          </Routes></PropertiesProvider>} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
